import type { LucideIcon } from "lucide-react";
import type { LineOfBusiness } from "@/lib/brokerProducts";
import type {
  WizardDefinition,
  WizardStep,
  WizardSubstep,
} from "@/base/wizard/types";

// Declares a line's wizard and checks it on the spot (BRK-S300), like
// defineListView: a definition that can't work fails in tests, not on a
// broker's screen.

/** Every line PMS has. A Record, so a line added to the contract's union fails
 *  to compile here until it is listed. */
const LINES: Readonly<Record<LineOfBusiness, true>> = {
  general_liability: true,
  workers_compensation: true,
  commercial_auto: true,
};

/** Lowercase words joined by single hyphens: a step id is the URL's last part. */
const URL_SAFE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** The query a page's URL carries when a link on the last page opened it. */
const RETURN_PARAM = "return";
const RETURN_TO_LAST = "last";

/**
 * One page of the wizard (BRK-S387): a step without sub-steps, or one
 * sub-step, in the order Back and Next walk them. A sub-step carries its
 * step's icon, and its step's description when it names none.
 */
export interface WizardPage<Draft> extends WizardSubstep<Draft> {
  /** The URL after the application's id: `step`, or `step/substep`. */
  path: string;
  /** Its step's index in the definition's steps. */
  step: number;
  icon?: LucideIcon;
}

/** The definition's steps as the pages Back and Next walk: each step's
 *  sub-steps in order, then the next step. */
export function wizardPages<Draft>(
  steps: readonly WizardStep<Draft>[],
): WizardPage<Draft>[] {
  return steps.flatMap((s, step): WizardPage<Draft>[] =>
    s.substeps
      ? s.substeps.map((sub) => ({
          ...sub,
          path: `${s.id}/${sub.id}`,
          step,
          icon: s.icon,
          description: sub.description ?? s.description,
        }))
      : [{ ...s, path: s.id, step }],
  );
}

export function defineWizard<Draft>(
  def: WizardDefinition<Draft>,
): WizardDefinition<Draft> {
  const where = `The "${def.title}" wizard`;
  if (!Object.prototype.hasOwnProperty.call(LINES, def.line)) {
    throw new Error(
      `${where} names "${def.line}", not a line of business PMS has.`,
    );
  }
  if (!def.basePath.startsWith("/") || def.basePath.endsWith("/")) {
    throw new Error(
      `${where} needs a base path that starts with "/" and doesn't end with one.`,
    );
  }
  if (def.steps.length < 2) {
    throw new Error(`${where} needs at least 2 steps.`);
  }
  const ids = def.steps.map((s) => s.id);
  for (const id of ids) {
    if (!URL_SAFE.test(id)) {
      throw new Error(
        `${where}'s step id "${id}" isn't URL-safe: use lowercase letters, digits and single hyphens.`,
      );
    }
  }
  if (new Set(ids).size !== ids.length) {
    throw new Error(`${where} needs unique step ids.`);
  }
  for (const s of def.steps) {
    if (!s.substeps) continue;
    if (s.substeps.length === 0) {
      throw new Error(`${where}'s step "${s.id}" needs at least one sub-step.`);
    }
    const subIds = s.substeps.map((sub) => sub.id);
    for (const id of subIds) {
      if (!URL_SAFE.test(id)) {
        throw new Error(
          `${where}'s sub-step id "${id}" isn't URL-safe: use lowercase letters, digits and single hyphens.`,
        );
      }
    }
    if (new Set(subIds).size !== subIds.length) {
      throw new Error(`${where}'s step "${s.id}" needs unique sub-step ids.`);
    }
  }
  for (const [former, now] of Object.entries(def.formerSteps ?? {})) {
    if (ids.includes(former)) {
      throw new Error(
        `${where}'s former step "${former}" is still a step: it can't name another.`,
      );
    }
    if (!ids.includes(now)) {
      throw new Error(
        `${where}'s former step "${former}" names "${now}", not a step it has.`,
      );
    }
  }
  return def;
}

/** The first page the draft hasn't completed; the last page once all are. A
 *  pending page never counts: it can't be completed yet, and must not hold the
 *  pages after it back. */
export function firstIncompleteIndex<Draft>(
  steps: readonly WizardPage<Draft>[],
  draft: Draft,
): number {
  const i = steps.findIndex((s) => !s.pending && !s.isComplete(draft));
  return i === -1 ? steps.length - 1 : i;
}

/**
 * Where a URL lands: the page it names when that page is reachable — done, or
 * the first not done — else the first incomplete page. A broker can go back to
 * anything finished but can't skip past an unfinished page. A URL naming a
 * step made of sub-steps names its first unfinished sub-step, or its first
 * once all are done (BRK-S387).
 */
export function landingIndex<Draft>(
  steps: readonly WizardPage<Draft>[],
  draft: Draft,
  requested?: string,
): number {
  const limit = firstIncompleteIndex(steps, draft);
  let named = steps.findIndex((s) => s.path === requested);
  if (named === -1 && requested) {
    const first = steps.findIndex((s) => s.path.startsWith(`${requested}/`));
    if (first !== -1) {
      const unfinished = steps.findIndex(
        (s, i) =>
          i >= first &&
          s.step === steps[first].step &&
          !s.pending &&
          !s.isComplete(draft),
      );
      named = unfinished === -1 ? first : unfinished;
    }
  }
  return named !== -1 && named <= limit ? named : limit;
}

/**
 * The steps that need attention (BRK-S307): not complete, with a later step
 * that is. The broker had passed them, and an earlier change undid them; the
 * stepper says so, and a resumed draft lands on the first. A pending step
 * can't be completed yet, so it never needs attention, and its own state
 * never counts.
 */
export function needsAttention<Draft>(
  steps: readonly WizardPage<Draft>[],
  draft: Draft,
): boolean[] {
  const done = steps.map((s) => !s.pending && s.isComplete(draft));
  return steps.map(
    (s, i) => !s.pending && !done[i] && done.slice(i + 1).some(Boolean),
  );
}

/** A page's URL as a link on the last page opens it (BRK-S307, BRK-S387),
 *  such as a missing answer's: that page offers "Save and return to …" in
 *  place of Next. */
export function editAndReturn(stepPath: string): string {
  return `${stepPath}?${RETURN_PARAM}=${RETURN_TO_LAST}`;
}

/** Whether a page's URL query says a link on the last page opened it. */
export function returnsToLast(search: string): boolean {
  return new URLSearchParams(search).get(RETURN_PARAM) === RETURN_TO_LAST;
}
